import { Component, DestroyRef, Inject, inject, Input, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgbActiveModal, NgbProgressbarModule } from "@ng-bootstrap/ng-bootstrap";
import { DataProvider } from "../../interfaces/data-provider.interface";
import { DATA_PROVIDER } from "../../tokens";
import { formatBytes } from "../../utils/format-bytes";
import { OfflineLayerService } from "../../../map/services/offline-layer.service";
import { LayerService } from "../../../map/services/layer.service";

export interface TileDownloadItem {
	key: string;
	name: string;
	status: 'pending' | 'downloading' | 'done';
	progress: number;
	max: number;
	sizeBytes?: number;
}

@Component({
	standalone: true,
	selector: 'wd-download-progress',
	templateUrl: './download-progress.component.html',
	imports: [CommonModule, NgbProgressbarModule]
})
export class DownloadProgressComponent implements OnInit {
	activeModal = inject(NgbActiveModal);
	ols = inject(OfflineLayerService);
	ls = inject(LayerService);
	destroyRef = inject(DestroyRef);

	// Flags: Downloads überspringen wenn bereits vollständig
	@Input() skipTileDownload = false;
	@Input() skipPointsDownload = false;

	// Anzahl der zu ladenden Punkte (von außen gesetzt)
	@Input() expectedPointsCount = 0;

	// Label für Punkte-Bereich (z.B. "Bohrpunkte", "Datenpunkte")
	@Input() pointsLabel = 'Datenpunkte';

	// Eigene Tile-Tracking-Liste die persistent bleibt
	tileItems = signal<TileDownloadItem[]>([]);

	// Punkte-Status
	pointsStatus = signal<'pending' | 'downloading' | 'done'>('pending');
	pointsProgress = signal<number>(0);
	loadedPointsCount = signal<number>(0);

	// Timeout-Status: Zeigt an ob der Download nicht gestartet werden konnte
	startupTimedOut = signal<boolean>(false);

	formatBytes = formatBytes;

	private updateInterval?: ReturnType<typeof setInterval>;
	private startupTimeout?: ReturnType<typeof setTimeout>;
	// Flag: Tile-Seeding ist abgeschlossen (auch wenn zu schnell für Polling)
	private seedingCompleted = false;

	constructor(@Inject(DATA_PROVIDER) public dataProvider: DataProvider) {}

	ngOnInit(): void {
		// Initialisiere Tile-Items aus MapDefinition (zeigt alle Layer sofort als "pending")
		if (!this.skipTileDownload) {
			this.initializeTileItems();
		}

		// Auf seedAllCompleted reagieren (wichtig wenn Seeding < 200ms dauert)
		const sub = this.ols.seedAllCompleted.subscribe(() => {
			this.seedingCompleted = true;
		});

		// Polling für Updates (alle 200ms)
		this.updateInterval = setInterval(() => {
			this.updateTileItems();
			this.updatePointsStatus();
		}, 200);

		// Timeout: Wenn nach 10 Sekunden kein Download gestartet wurde, Warnung anzeigen
		this.startupTimeout = setTimeout(() => {
			const tilesExpected = !this.skipTileDownload;
			const pointsExpected = !this.skipPointsDownload;
			// Prüfe ob mindestens ein Layer gestartet wurde (nicht mehr pending)
			const tilesStarted = this.tileItems().some(t => t.status !== 'pending');
			const pointsStarted = this.pointsStatus() !== 'pending';

			if ((tilesExpected && !tilesStarted) || (pointsExpected && !pointsStarted)) {
				this.startupTimedOut.set(true);
			}
		}, 10000);

		this.destroyRef.onDestroy(() => {
			sub.unsubscribe();
			if (this.updateInterval) {
				clearInterval(this.updateInterval);
			}
			if (this.startupTimeout) {
				clearTimeout(this.startupTimeout);
			}
		});
	}

	/**
	 * Initialisiert die Tile-Items aus der MapDefinition.
	 * Zeigt alle erwarteten Layer sofort als "pending" an.
	 */
	private initializeTileItems(): void {
		const mapDef = this.ls.mapDef();
		if (!mapDef?.layers) return;

		const items: TileDownloadItem[] = [];

		for (const layer of mapDef.layers) {
			// Nur angezeigte Tile-Layer (WMS, OSM)
			if (layer.display === false) continue;
			if (layer.type !== 'wms' && layer.type !== 'osm') continue;

			// Name aus Layer-Definition
			const name = layer.name || 'Unbenannt';

			items.push({
				key: `pending_${name}`, // Temporärer Key, wird später aktualisiert
				name,
				status: 'pending',
				progress: 0,
				max: 100
			});
		}

		this.tileItems.set(items);
	}

	private updateTileItems(): void {
		const seedData = this.ols.seedData();
		let currentItems = [...this.tileItems()];
		const seedKeys = new Set(seedData.keys());
		const matchedPendingIndices = new Set<number>();

		// Aktualisiere oder füge neue Items hinzu
		seedData.forEach((value, key) => {
			const progress = this.ols.seedCurrentProgress(value);
			const max = this.ols.seedMaxProgress(value);
			// Name aus Key extrahieren (Format: projectId_layerName)
			const name = key.substring(key.lastIndexOf('_') + 1);

			// Suche nach existierendem Item mit diesem Key (bereits aktiv)
			let existingIndex = currentItems.findIndex(item => item.key === key);

			if (existingIndex < 0) {
				// Suche erstes pending Item (FIFO-Reihenfolge)
				existingIndex = currentItems.findIndex((item, idx) =>
					item.status === 'pending' && !matchedPendingIndices.has(idx)
				);
			}

			if (existingIndex >= 0 && currentItems[existingIndex].status === 'pending') {
				matchedPendingIndices.add(existingIndex);
			}

			if (existingIndex >= 0) {
				// Update existing
				currentItems[existingIndex] = {
					...currentItems[existingIndex],
					key,
					name,
					progress,
					max,
					sizeBytes: value.size,
					status: progress >= max ? 'done' : 'downloading'
				};
			} else {
				// Sollte nicht passieren, aber sicherheitshalber
				currentItems.push({
					key,
					name,
					status: 'downloading',
					progress,
					max,
					sizeBytes: value.size
				});
			}
		});

		// Markiere Items als done wenn sie aus seedData verschwinden
		for (let i = 0; i < currentItems.length; i++) {
			if (currentItems[i].status === 'downloading' && !seedKeys.has(currentItems[i].key)) {
				currentItems[i] = { ...currentItems[i], status: 'done', progress: currentItems[i].max };
			}
		}

		// Wenn Seeding beendet ist: pending Items als done markieren
		// seedingCompleted fängt den Fall ab, wenn Seeding < 200ms dauert (kein Polling-Treffer)
		if (!this.ols.isSeeding() && seedKeys.size === 0 &&
			(this.seedingCompleted || currentItems.some(i => i.status !== 'pending'))) {
			currentItems = currentItems.map(item =>
				item.status === 'pending' ? { ...item, status: 'done' as const, progress: item.max } : item
			);
		}

		this.tileItems.set(currentItems);
	}

	private updatePointsStatus(): void {
		const isBusy = this.dataProvider.isBusy;
		const progress = this.dataProvider.loadingProgress;
		const progressText = this.dataProvider.loadingProgressText;

		if (isBusy) {
			this.pointsStatus.set('downloading');
			this.pointsProgress.set(progress);
			// Extrahiere aktuelle/Gesamt-Anzahl aus "123 / 456 Punkte gelesen"
			const match = progressText?.match(/^(\d+)\s*\/\s*(\d+)/);
			if (match) {
				// Während Download: Zeige Gesamt (match[2])
				this.loadedPointsCount.set(parseInt(match[2], 10));
			}
		} else if (this.pointsStatus() === 'downloading' || progress >= 100) {
			// Beim Abschluss: Hole finale Anzahl aus DataProvider
			const finalCount = this.dataProvider.lastDownloadedPointsCount;
			if (finalCount > 0) {
				this.loadedPointsCount.set(finalCount);
			} else if (this.loadedPointsCount() === 0 && progressText) {
				// Fallback: aus letztem progressText extrahieren
				const match = progressText.match(/^(\d+)\s*\/\s*(\d+)/);
				if (match) {
					this.loadedPointsCount.set(parseInt(match[2], 10));
				}
			}
			this.pointsStatus.set('done');
			this.pointsProgress.set(100);
		}
	}

	get isDownloading(): boolean {
		const tilesDownloading = !this.skipTileDownload && this.ols.isSeeding();
		const pointsDownloading = !this.skipPointsDownload && this.dataProvider.isBusy;
		return tilesDownloading || pointsDownloading;
	}

	get allDone(): boolean {
		// Tiles: done wenn übersprungen, oder Seeding beendet und alle Items done (oder keine Items mehr)
		const items = this.tileItems();
		const tilesDone = this.skipTileDownload ||
			(!this.ols.isSeeding() && (items.length === 0 || items.every(t => t.status === 'done')));

		// Punkte: done wenn übersprungen oder fertig
		const pointsDone = this.skipPointsDownload || this.pointsStatus() === 'done';

		return tilesDone && pointsDone;
	}

	get hasErrors(): boolean {
		return false;
	}

	get wasCancelled(): boolean {
		return this.ols.isCancelRequested();
	}

	getStatusIcon(status: string): string {
		switch (status) {
			case 'pending': return 'bi-hourglass';
			case 'downloading': return 'bi-arrow-repeat';
			case 'done': return 'bi-check-circle-fill text-success';
			case 'error': return 'bi-x-circle-fill text-danger';
			default: return 'bi-hourglass';
		}
	}

	onCancel(): void {
		this.ols.cancelSeeding();
	}

	onClose(): void {
		this.activeModal.close(this.wasCancelled ? 'cancelled' : (this.hasErrors ? 'error' : 'success'));
	}
}
